import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Music2, Headphones } from "lucide-react";
import { api, imgSrc } from "../lib/api";

function useCountdown() {
  const [target, setTarget] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    api
      .get("/podcast/next-friday")
      .then((r) => setTarget(new Date(r.data.next_friday)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  if (!target) return { days: "--", hours: "--", minutes: "--", seconds: "--" };
  const diff = Math.max(0, target.getTime() - Date.now());
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return {
    days: String(days).padStart(2, "0"),
    hours: String(hours).padStart(2, "0"),
    minutes: String(minutes).padStart(2, "0"),
    seconds: String(seconds).padStart(2, "0"),
    _tick: tick,
  };
}

export default function PodcastPlayer({ episode, settings }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dur, setDur] = useState(0);
  const cd = useCountdown();

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setProgress(el.currentTime);
    const onDur = () => setDur(el.duration || 0);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onDur);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onDur);
    };
  }, [episode?.id]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      el.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  const seek = (pct) => {
    const el = audioRef.current;
    if (!el || !dur) return;
    el.currentTime = (pct / 100) * dur;
  };

  const fmt = (s) => {
    if (!Number.isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const ss = String(Math.floor(s % 60)).padStart(2, "0");
    return `${m}:${ss}`;
  };

  if (!episode) return null;

  return (
    <section
      data-testid="podcast-player-section"
      className="bg-[#D9C5FD] border-y-2 border-black"
    >
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 flex flex-col gap-6">
          <span className="pill-tag w-fit bg-[#FFD01E]">
            {settings?.podcast_kicker || "Podcast · tous les vendredis"}
          </span>
          <h2 className="font-display-black text-5xl sm:text-7xl">
            {settings?.podcast_title_1 || "BON SANG"} <span className="text-[#FA6157]">!</span>
            <br />
            {settings?.podcast_title_2 || "le podcast."}
          </h2>
          <p className="text-base max-w-xl">
            {settings?.podcast_intro ||
              "Des conversations intimes sans filtre, chaque vendredi à 8h. Branche tes oreilles : ça pique et ça console."}
          </p>

          {/* Countdown */}
          <div
            data-testid="podcast-countdown"
            className="brutal-card bg-[#FFFDF7] p-5 flex flex-col gap-3"
          >
            <p className="uppercase text-xs font-bold tracking-widest">
              Prochain épisode dans
            </p>
            <div className="flex gap-3">
              {[
                ["JOURS", cd.days],
                ["H", cd.hours],
                ["MIN", cd.minutes],
                ["SEC", cd.seconds],
              ].map(([l, v]) => (
                <div
                  key={l}
                  className="flex-1 border-2 border-black rounded-xl bg-[#0A8074] text-[#FFD01E] py-3 text-center"
                >
                  <div className="font-display-black text-3xl sm:text-5xl">{v}</div>
                  <div className="text-[10px] uppercase font-bold tracking-widest">
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Listen-on */}
          <div className="flex flex-wrap gap-3">
            <a
              href={episode.spotify_url}
              target="_blank"
              rel="noreferrer"
              data-testid="listen-spotify"
              className="brutal-btn brutal-btn-teal"
            >
              <Music2 size={16} /> Spotify
            </a>
            <a
              href={episode.apple_url}
              target="_blank"
              rel="noreferrer"
              data-testid="listen-apple"
              className="brutal-btn brutal-btn-coral"
            >
              <Headphones size={16} /> Apple Podcasts
            </a>
            <a
              href={episode.deezer_url}
              target="_blank"
              rel="noreferrer"
              data-testid="listen-deezer"
              className="brutal-btn brutal-btn-yellow"
            >
              <Headphones size={16} /> Deezer
            </a>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="brutal-card bg-[#FFFDF7] p-5 flex flex-col gap-4">
            <div className="relative aspect-square border-2 border-black rounded-xl overflow-hidden">
              <img
                src={imgSrc(episode.cover_image)}
                alt={episode.title}
                className="w-full h-full object-cover"
              />
              <button
                data-testid="podcast-play-btn"
                onClick={toggle}
                className="absolute bottom-4 right-4 w-16 h-16 rounded-full bg-[#FFD01E] border-2 border-black flex items-center justify-center shadow-[4px_4px_0_#050505] hover:translate-y-[-2px] transition"
                aria-label={playing ? "Pause" : "Play"}
              >
                {playing ? <Pause size={28} strokeWidth={2.5} /> : <Play size={28} strokeWidth={2.5} />}
              </button>
            </div>
            <div>
              <p className="text-xs uppercase font-bold tracking-widest text-black/60">
                Épisode #{episode.number}
              </p>
              <h3 className="font-display text-2xl mt-1">{episode.title}</h3>
              <p className="text-sm text-black/70 mt-2 line-clamp-3">
                {episode.description}
              </p>
            </div>
            {/* Scrubber */}
            <div className="flex items-center gap-3 text-xs font-bold">
              <span>{fmt(progress)}</span>
              <input
                data-testid="podcast-scrubber"
                type="range"
                min={0}
                max={100}
                value={dur ? (progress / dur) * 100 : 0}
                onChange={(e) => seek(Number(e.target.value))}
                className="flex-1 accent-[#FA6157]"
              />
              <span>{fmt(dur || 0)}</span>
            </div>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => audioRef.current && (audioRef.current.currentTime -= 15)}
                className="brutal-btn brutal-btn-cream !py-2 !px-3"
                data-testid="podcast-back-btn"
              >
                <SkipBack size={16} />
              </button>
              <button
                onClick={toggle}
                className="brutal-btn brutal-btn-coral !py-2 !px-5"
                data-testid="podcast-toggle-btn"
              >
                {playing ? <Pause size={16} /> : <Play size={16} />}{" "}
                {playing ? "Pause" : "Écouter"}
              </button>
              <button
                onClick={() => audioRef.current && (audioRef.current.currentTime += 15)}
                className="brutal-btn brutal-btn-cream !py-2 !px-3"
                data-testid="podcast-forward-btn"
              >
                <SkipForward size={16} />
              </button>
            </div>
            <audio ref={audioRef} src={episode.audio_url} preload="metadata" />
          </div>
        </div>
      </div>
    </section>
  );
}
